import { createAsyncThunk } from "@reduxjs/toolkit";
import axios, { AxiosResponse } from "axios";
import { DEFAULT_ITEMS_PER_PAGE, JOB_LISTING_DEFAULT_ITEMS_PER_PAGE } from "../../constants/app";
import { TToken } from "../../models";
import { setTokenAction } from "../auth/slice";
import { setAvailabilityAction } from "../onboarding/slice";
import { getProfileDetailsThunk } from "../onboarding/thunk";
import { setCandidateAcceptedJobsList, setCandidateCalendarData, setCandidateDayCalendarData, setCandidateHistoryJobsList, setCandidateJobsList, setCandidateNotificationSettings, setCandidatePendingJobsList, setCandidatesJobDetailsOverCantresListAction } from "./slice";
import { GetCandidateCentresResponse, ICandidateSaveSettingsInput, TApplyForAJobResponse, TCandidateNotifiationSettingsResponse, TGetCandidateAcceptedListingResponse, TGetCandidateHistoryListingResponse, TGetCandidateJobListingResponse, TGetCandidatePendingListingResponse, TGetCandidatesJobsOverCentresResponse, TMakeCentrePrefferedResponse, TSaveCalendarDataInput } from "./types";


export const getCandidateJobListings = createAsyncThunk(
  "vacancy/getCandidateJobListings",
  async ( 
      {  page = 1, per_page = JOB_LISTING_DEFAULT_ITEMS_PER_PAGE, sort_order="asc", sort_by, distance_away }: 
      {  page?: string | number | null | Array<number>; per_page?: number; sort_order: string, sort_by?: string, distance_away?: number }
      , { rejectWithValue, dispatch }) => {
    try {
      const response: AxiosResponse<TGetCandidateJobListingResponse> = await axios.get(
        `/v1/candidates/jobs`,
        {
          params: { page: page || 1, per_page: per_page || JOB_LISTING_DEFAULT_ITEMS_PER_PAGE,sort_order: sort_order, sort_by: sort_by, distance_away: distance_away }
        }
      );
      if (response.data?.data?.token) {
        await dispatch(setTokenAction(response.data.data.token));
      }
      if (response.data.data.job_listing) {
        await dispatch(setCandidateJobsList(response.data.data));
      }
      return response.data.data.job_listing;
    } catch (err) {
      return rejectWithValue((err as any).response.data || err);
    }
  }
);


export const getCandidatePendingListings = createAsyncThunk(
  "vacancy/getCandidatePendingListings",
  async ( 
      {  page = 1, per_page = DEFAULT_ITEMS_PER_PAGE, sort_order="asc", sort_by }: 
      {  page?: string | number | null | Array<number>; per_page?: number; sort_order: string, sort_by?: string }
      , { rejectWithValue, dispatch }) => {
    try {
      const response: AxiosResponse<TGetCandidatePendingListingResponse> = await axios.get(
        `/v1/candidates/jobs/pending`,
        {
          params: { page: page || 1, per_page: per_page || DEFAULT_ITEMS_PER_PAGE,sort_order: sort_order, sort_by: sort_by }
        }
      );
      if (response.data?.data?.token) {
        await dispatch(setTokenAction(response.data.data.token));
      }
      if (response.data.data.pending_jobs) {
        await dispatch(setCandidatePendingJobsList(response.data.data));
      }
      return response.data.data.pending_jobs;
    } catch (err) {
      return rejectWithValue((err as any).response.data || err);
    }
  }
);


export const getCandidateAcceptedListings = createAsyncThunk(
  "vacancy/getCandidateAcceptedListings",
  async ( 
      {  page = 1, per_page = DEFAULT_ITEMS_PER_PAGE, sort_order="asc", sort_by }: 
      {  page?: string | number | null | Array<number>; per_page?: number; sort_order: string, sort_by?: string }
      , { rejectWithValue, dispatch }) => {
    try {
      const response: AxiosResponse<TGetCandidateAcceptedListingResponse> = await axios.get(
        `/v1/candidates/jobs/accepted`,
        {
          params: { page: page || 1, per_page: per_page || DEFAULT_ITEMS_PER_PAGE,sort_order: sort_order, sort_by: sort_by }
        }
      );
      if (response.data?.data?.token) {
        await dispatch(setTokenAction(response.data.data.token));
      }
      if (response.data.data.accepted_jobs) {
        await dispatch(setCandidateAcceptedJobsList(response.data.data));
      }
      return response.data.data.accepted_jobs;
    } catch (err) {
      return rejectWithValue((err as any).response.data || err);
    }
  }
);

export const getCandidateHistoryListings = createAsyncThunk(
  "vacancy/getCandidateHistoryListings",
  async ( 
      {  page = 1, per_page = DEFAULT_ITEMS_PER_PAGE, sort_order="asc", sort_by, positions= [], keyword= "", centre_ids= [] }: 
      {  page?: string | number | null | Array<number>; per_page?: number; sort_order: string, sort_by?: string, positions?: number[], keyword?: string, centre_ids?: number[] }
      , { rejectWithValue, dispatch }) => {
    try {      
      const queryParams = new URLSearchParams();

      if (sort_by) queryParams.append("sort_by", sort_by);
      if (sort_order) queryParams.append("sort_order", sort_order);
      if (keyword) queryParams.append("keyword", keyword);
      if (typeof page === "number") queryParams.append("page", String(page));
      if (typeof per_page === "number")
        queryParams.append("per_page", String(per_page));


      const baseUrl = "/v1/candidates/jobs/history";
      const url = `${baseUrl}?${queryParams.toString()}`;

      // Append raw array-style query strings manually
      const centrePart = centre_ids?.length
        ? `&centre_ids=[${centre_ids.join(",")}]`
        : "";
      const positionPart = positions?.length
        ? `&positions=[${positions.join(",")}]`
        : "";

      const finalUrl = `${url}${centrePart}${positionPart}`;

      const response: AxiosResponse<TGetCandidateHistoryListingResponse> = await axios.get(finalUrl);
      if (response.data?.data?.token) {
        await dispatch(setTokenAction(response.data.data.token));
      }
      if (response.data.data.history_jobs) {
        await dispatch(setCandidateHistoryJobsList(response.data.data));
      }
      return response.data.data.history_jobs;
    } catch (err) {
      return rejectWithValue((err as any).response.data || err);
    }
  }
);

export const getCandidateCentres = createAsyncThunk(
  'vacancy/getCandidateCentres',
  async (_, { dispatch, rejectWithValue }) => {
    try {
      const response: AxiosResponse<GetCandidateCentresResponse> = await axios.get('/v1/working-centres');

      // Optional token handling
      if ('token' in response.data.data) {
        await dispatch(setTokenAction(response.data.data.token as TToken));
      }

      return response.data;
    } catch (err) {
      return rejectWithValue((err as any).response?.data || err);
    }
  }
);

export const applyForAJobThunk = createAsyncThunk(
  'vacancy/applyForAJobThunk',
  async (data: FormData, { dispatch, rejectWithValue }) => {
    try {
      const response: AxiosResponse<TApplyForAJobResponse> = await axios.post(`/v1/candidates/jobs/update-application-status`, data)
      if ("token" in response.data.data) {
        await dispatch(setTokenAction(response.data.data.token));
      }
      return response.data
    } catch (err) {
      return rejectWithValue((err as any).response.data || err)
    }
  }
)

export const makeCentrePrefferedThunk = createAsyncThunk(
  'vacancy/makeCentrePrefferedThunk',
  async ({centre_id, centre_uuid}: {centre_id: number, centre_uuid: string},  { dispatch, rejectWithValue }) => {
    try {
      const response: AxiosResponse<TMakeCentrePrefferedResponse> = await axios.post(`/v1/candidates/preferred-centre`, {centre_id: centre_id, centre_uuid: centre_uuid})
      await dispatch(setTokenAction(response.data.data.token))
      return response.data
    } catch (err) {
      return rejectWithValue((err as any).response.data || err)
    }
  }
)

export const removeCentrePrefferedThunk = createAsyncThunk(
  'vacancy/removeCentrePrefferedThunk',
  async ({centre_id, centre_uuid}: {centre_id: number, centre_uuid: string},  { dispatch, rejectWithValue }) => {
    try {
      const response: AxiosResponse<TMakeCentrePrefferedResponse> = await axios.post(`/v1/candidates/preferred-centre`, {centre_id: centre_id, action: "remove", centre_uuid: centre_uuid})
      await dispatch(setTokenAction(response.data.data.token))
      return response.data
    } catch (err) {
      return rejectWithValue((err as any).response.data || err)
    }
  }
)

// ----------------- Candidate Calendar Data Thunk -----------------
/**
 * Thunk to get the candidate's calendar data.
 */
export const getCandidateCalendarData = createAsyncThunk(    
  'vacancy/getCandidateCalendarData',
  async (data: { date?: string; type?: string; distance_away?: number }, { rejectWithValue, dispatch }) => {
    try {
      const params: Record<string, any> = {};
      if (data?.date) params.date = data.date;
      if (data?.type) params.type = data.type;
      if (data?.distance_away) params.distance_away = data.distance_away;
      const response: AxiosResponse<any> = await axios.get(`/v1/candidates/jobs-calandar`, { params });
      await dispatch(setTokenAction(response.data.data.token))
      // if (Object.keys(params).length === 0) {
      //   await dispatch(setCandidateCalendarData(response.data.data));
      // }
      if (Object.keys(params).length === 0 || Object.keys(params).length === 1) {
        await dispatch(setCandidateCalendarData(response.data.data));
      }else{
        await dispatch(setCandidateDayCalendarData(response.data.data.jobs));
      }
      return response.data
    } catch (err) {
      return rejectWithValue((err as any).response.data || err)
    }
  }
)

export const calendarAvailabilitiesThunk = createAsyncThunk(
  'vacancy/calendarAvailabilitiesThunk',
  async (data: TSaveCalendarDataInput, { rejectWithValue, dispatch }) => {
    try {
      const responseAvailabilities: AxiosResponse<any> = await axios.post('/v1/candidates/availabilities', data)
      await dispatch(setTokenAction(responseAvailabilities.data.data.token))
      await dispatch(setAvailabilityAction(responseAvailabilities.data.data))
      return responseAvailabilities.data
    } catch (err) {
      return rejectWithValue((err as any).response.data || err)
    }
  }
)
// ----------------- Candidate Calendar Data Thunk -----------------
/**
 * GET method
 * getCandidatesJobsOverCentresThunk - To get candidates job details over centres
 */
export const getCandidatesJobsOverCentresThunk = createAsyncThunk(    
  'candidates/getCandidatesJobsOverCentresThunk',
  async (
    { keyword = '', page, per_page, sort_by="first_name", sort_order = "asc", centre_with_current_listing=false, centres_worked_previously=false, distance_away }: 
    { keyword?: string; page?: string | number | null | Array<number>; per_page: number, sort_by: string; sort_order: string; centre_with_current_listing: boolean; centres_worked_previously: boolean; distance_away: string | number | null }, 
    { rejectWithValue, dispatch }
  ) => {
    try {
      const response: AxiosResponse<TGetCandidatesJobsOverCentresResponse> = await axios.get(`/v1/candidates/centres`, {
        params: { keyword, page: page, per_page: per_page, sort_by: sort_by, sort_order: sort_order, centre_with_current_listing: centre_with_current_listing, centres_worked_previously: centres_worked_previously, distance_away: distance_away }
      });
      await dispatch(setTokenAction(response.data.data.token));
      await dispatch(setCandidatesJobDetailsOverCantresListAction(response.data.data.centres));
      return response.data;
    } catch (err) {
      return rejectWithValue((err as any).response.data || err);
    }
  }
);




export const getCandidateSettingsDataThunk = createAsyncThunk(    
  'candidates/getCandidateSettingsDataThunk',
  async (_, { rejectWithValue, dispatch }) => {
    try {
      const response: AxiosResponse<TCandidateNotifiationSettingsResponse> = await axios.get(`/v1/notification-settings`)
      await dispatch(setTokenAction(response.data.data.token))

      if(response.data.data.user_settings){
        await dispatch(setCandidateNotificationSettings(response.data.data?.user_settings))
      }      
      return response.data
    } catch (err) {
      return rejectWithValue((err as any).response.data || err)
    }
  }
)

export const submitCandidateNotificationSettings = createAsyncThunk(
  'candidates/submitCandidateNotificationSettings',
  async (data: ICandidateSaveSettingsInput, { rejectWithValue, dispatch }) => {
    try {
      const responseAvailabilities: AxiosResponse<any> = await axios.post('/v1/notification-settings', data)
      await dispatch(setTokenAction(responseAvailabilities.data.data.token))
      return responseAvailabilities.data
    } catch (err) {
      return rejectWithValue((err as any).response.data || err)
    }
  }
)

export const updateLocationPreferences = createAsyncThunk(
  'candidates/updateLocationPreferences',
  async (max_travel_distance: number, { rejectWithValue, dispatch }) => {
    try {
      const response: AxiosResponse<any> = await axios.post('/v1/candidates/location-preferences', { max_travel_distance })
      if (response.data?.data?.token) {
        await dispatch(setTokenAction(response.data.data.token))
      }
      // Refresh profile data after successful update
      await dispatch(getProfileDetailsThunk())
      return response.data
    } catch (err) {
      return rejectWithValue((err as any).response.data || err)
    }
  }
)

